import Typography from '@ui/Typography';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '@ui/Input';
import Checkbox from '@ui/Checkbox';
import Button from '@ui/Button';
import TextArea from '@ui/TextArea';
import { cx } from 'cva';
import getSymbolFromCurrency from 'currency-symbol-map';

const schemaPublic = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Email must be a valid email id'),
});

const schemaCommon = yup.object().shape({
  amount: yup
    .number()
    .required()
    .typeError('Amount must be a number')
    .min(1, `Minimum amount is 1`)
    .test(
      'giftOfGivingBalance',
      'You cannot put greater amount than your current gift of giving balance',
      function (value) {
        const giftOfGivingBalance = this.options.context.giftOfGivingBalance;
        return value <= giftOfGivingBalance;
      },
    ),
  tip: yup.array().of(yup.number().min(0).max(100)).required(),
  doDedicate: yup.boolean().notRequired().nullable(),
  dedicatedToName: yup.string().when('doDedicate', {
    is: true,
    then: yup.string().required('Dedicated to name is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  dedicatedToEmail: yup.string().when('doDedicate', {
    is: true,
    then: yup
      .string()
      .required('Dedicated to email is required')
      .email('Dedicated to email must be a valid email'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  dedicatedToMessage: yup.string().when('doDedicate', {
    is: true,
    then: yup.string().required('Dedicated to message is required'),
    otherwise: yup.string().notRequired().nullable(),
  }),
  isAnonymous: yup.boolean().notRequired().nullable(),
  shareNameAndEmail: yup.boolean().notRequired().nullable(),
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
});

function GiftOfGivingDonationForm(props) {
  const {
    amount = '',
    onSubmit,
    isLoggedIn,
    nonProfit = {},
    user = {},
  } = props;

  const [amountTab, setAmountTab] = useState(null);

  const schema = isLoggedIn()
    ? schemaCommon
    : schemaPublic.concat(schemaCommon);

  const currencySymbol =
    getSymbolFromCurrency(user?.giftOfGiving?.currency) || '$';
  const giftOfGivingBalance =
    user?.giftOfGiving?.allocatedAmount - user?.giftOfGiving?.usedAmount || 0;

  const giftAmount =
    Number(amount) > Number(giftOfGivingBalance)
      ? giftOfGivingBalance
      : Number(amount);

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      amount: giftAmount,
      tip: [30],
      doDedicate: false,
      dedicatedToName: '',
      dedicatedToEmail: '',
      dedicatedToMessage: '',
      isAnonymous: false,
      shareNameAndEmail: true,
      agreeTerms: false,
    },
    resolver: yupResolver(schema),
    context: { giftOfGivingBalance, currencySymbol },
    mode: 'onChange',
    setValue,
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    trigger,
  } = form;

  const [amountVal, nekiTip, doDedicate] = watch([
    'amount',
    'tip',
    'doDedicate',
  ]);
  const donationAmount = Math.max(0, Number(amountVal));

  const handleAmountTabChange = (tab) => {
    setAmountTab(tab);
    setValue('amount', `${tab}`);
    trigger();
  };

  const handleFormSubmit = (data) => {
    const payload = {
      ...data,
      isAnonymous: !data.shareNameAndEmail,
    };
    onSubmit(payload);
  };

  return (
    <div className="flex w-full flex-1 flex-col items-start justify-start">
      {/* <Typography variant="h1" className="mb-6" component="h2">
        Make a Donation
      </Typography> */}

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex w-full flex-col justify-start gap-8 lg:flex-row lg:justify-between">
        <div className="flex w-full shrink-0 grow-0 flex-col gap-6 lg:w-6/12">
          {/* <div className={`${!isLoggedIn() ? 'mt-[-20px]' : ''}`}>
            <Typography
              variant="h5"
              // color="textSecondary"
              className={`mt-5 uppercase`}>
              select your gift amount
            </Typography>
            <div className="mt-1 flex gap-4">
              <Button
                onClick={() => handleAmountTabChange(10)}
                variant={amountTab === 10 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 10
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                {currencySymbol}10
              </Button>
              <Button
                onClick={() => handleAmountTabChange(25)}
                variant={amountTab === 25 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 25
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                {currencySymbol}25
              </Button>
              <Button
                onClick={() => handleAmountTabChange(50)}
                variant={amountTab === 50 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 50
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                {currencySymbol}50
              </Button>
              <Button
                onClick={() => handleAmountTabChange(100)}
                variant={amountTab === 100 ? 'filled' : 'outlined'}
                color="transparent"
                className={cx(
                  amountTab === 100
                    ? 'bg-chips-secondary text-text-primary'
                    : 'bg-transparent text-secondary',
                  'w-full border border-chips-secondary font-dmsans text-14 font-medium hover:bg-chips-secondary hover:text-text-primary',
                )}>
                {currencySymbol}100
              </Button>
            </div>
          </div> */}
          <Typography variant="body1" color="textSecondary" className="">
            Use {currencySymbol}
            {giftOfGivingBalance} in your Gift of Giving Balance to benefit the
            nonprofit
          </Typography>

          <Controller
            name="amount"
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <Input
                label="GIFT AMOUNT"
                placeholder="Enter an Amount"
                required
                {...rest}
                adornment={
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="!font-medium">
                    {currencySymbol}
                  </Typography>
                }
                error={errors.amount?.message}
              />
            )}
          />
          {/* <Typography
            variant="caption"
            color="textSecondary"
            className="!font-medium">
            * You have {currencySymbol}
            {giftOfGivingBalance} in your Gift of Giving Balance
          </Typography> */}
        </div>
        <div className="flex flex-1 flex-col justify-between gap-2.5">
          <div className="flex flex-col gap-2.5">
            <Typography variant="h5" className="uppercase">
              Summary
            </Typography>

            <div className="flex">
              <Typography variant="body1" color="textSecondary">
                Your Total Gift of Giving Amount
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                className="ml-auto">
                {currencySymbol}
                {donationAmount.toFixed(2)}
              </Typography>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {/* <div className="flex items-center gap-4">
              <Controller
                name="shareNameAndEmail"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    defaultChecked
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-black">
                I want to share my name and email with the charity
              </Typography>
            </div> */}
            <div className="flex items-center gap-4">
              <Controller
                name="doDedicate"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <Checkbox
                    {...rest}
                    className="border-text-disabled font-extrabold"
                  />
                )}
              />

              <Typography
                variant="body2"
                color="textDisabled"
                className="font-bold">
                Dedicate this donation to someone
              </Typography>
            </div>

            {doDedicate && (
              <div className="flex flex-col gap-4">
                <Controller
                  name="dedicatedToName"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Name"
                      required
                      {...rest}
                      error={errors.dedicatedToName?.message}
                    />
                  )}
                />
                <Controller
                  name="dedicatedToEmail"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      label="Email Id"
                      required
                      {...rest}
                      error={errors.dedicatedToEmail?.message}
                    />
                  )}
                />
                <Controller
                  name="dedicatedToMessage"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <TextArea
                      label="Message"
                      required
                      {...rest}
                      error={errors.dedicatedToMessage?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <Controller
              name="agreeTerms"
              control={control}
              render={({ field: { ref, ...rest } }) => (
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-4">
                    <Checkbox
                      {...rest}
                      error={errors.agreeTerms?.message}
                      className="border-text-disabled font-extrabold"
                    />
                    <Typography
                      variant="body2"
                      color="textDisabled"
                      className="font-bold">
                      I agree to the{' '}
                      <Typography
                        variant="body2"
                        component="a"
                        color="hyperlink"
                        target="_blank"
                        href="https://www.neki.io/terms-and-conditions"
                        className="text-primary hover:underline">
                        Terms of Service
                      </Typography>{' '}
                      and{' '}
                      <Typography
                        variant="body2"
                        component="a"
                        color="hyperlink"
                        target="_blank"
                        href="https://www.neki.io/privacy-policy"
                        className="text-primary hover:underline">
                        Privacy Policy
                      </Typography>
                    </Typography>
                  </div>
                  <Typography
                    variant="body2"
                    color="error"
                    className={!errors.agreeTerms && 'hidden'}>
                    Please agree to proceed
                  </Typography>
                </div>
              )}
            />

            <Button
              type="submit"
              variant="filled"
              color="primary"
              className="flex w-full items-center justify-center">
              Give To Charity
            </Button>
          </div>
        </div>
      </form>

      <Typography
        variant="caption"
        color="textSecondary"
        className="mt-8 pb-14 text-justify lg:pb-0">
        Your contribution will be processed by your organization as part of the
        gift-of-giving program. The donation made by your organization will be
        going directly to an IRS-registered 501(c)(3) charity, subject to its
        terms. The charity receives the donation, which is typically tax
        deductible, minus payment processor&apos;s processing fees of 2.9% +
        $0.30 and a 2% Neki platform fee. If the charity is not on the Neki
        platform, we invite them to claim their profile to receive the donation
        amount. If the charities do not claim their profile within 45-60 days,
        we would not be able to fund your chosen charity, it will ask your
        organization admin to recommend other, and grant the funds to a similar
        charity.
      </Typography>
    </div>
  );
}

export default GiftOfGivingDonationForm;
